/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, signal} from '@angular/core';
import * as _ from 'lodash';
import * as moment from 'moment';
import {AppEventBusService} from '../events/app-event-bus.service';

import {deferMarkForCheck} from '../util/defer-mark-for-check';
import {createBufferedFlag, readBufferedFlag} from '../util/template-state';
import template from './top-domain-table.component.html';

import {OBJECT_TYPE} from '../constants/object-type';
import {StatsResource} from '../../../components/api/resources/resource/stats-resources/stats-resource';
import {Downloader} from '../../../components/util/downloader';
import {OgTranslateService} from '../filters/og-translate.service';
import {AdnLogService} from '../../../components/util/adn-log.service';
import {ChartOptionsUtil} from '../options/chart-options-util.service';

@Component({
  standalone: false,
  selector: 'adn-top-domain-table',
  template
})
export class TopDomainTableComponent implements OnInit, OnChanges, OnDestroy {
  @Input() object: any;
  @Input() parentType: string;
  @Input() dateSelectorId: string;

  sorting = {
    orderByParam: 'impressions',
    asc: true
  };

  parentParam: string;
  loaded = false;
  private readonly loadedFlag = createBufferedFlag();
  private destroyed = false;
  error = false;
  dataTotals: any[] = null;
  targetingExpression = false;
  fields: string[] = [];
  customFields: string[] = [];
  readonly startDate = signal<any>('UNDEFINED');
  readonly endDate = signal<any>(undefined);
  canceller: any;

  downloadFields: string[];
  downloadColumnOrder: string[];
  csvTableTitle: string;

  private chartDateListener: () => void;
  private allFields: any[];
  private chartOptionObject: any;
  private params: any = {};
  private configured = false;

  get resolvedParentType(): string {
    return this.parentType;
  }

  get resolvedDateSelectorId(): string {
    return this.dateSelectorId;
  }

  get displayLoaded(): boolean {
    return readBufferedFlag(this.loadedFlag, this.loaded, this.cdr, () => !this.destroyed);
  }

  constructor(
    private readonly statsResource: StatsResource,
    private readonly eventBus: AppEventBusService,
    private readonly adnLog: AdnLogService,
    private readonly ogTranslate: OgTranslateService,
    @Inject(ChartOptionsUtil) private readonly ChartOptionsUtil: any,
    private readonly cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit(): void {
    this.tryInit();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.parentType || changes.dateSelectorId || changes.object) {
      this.tryInit();
    }
  }

  private tryInit(): void {
    this.chartOptionObject = OBJECT_TYPE[this.resolvedParentType];
    if (!this.chartOptionObject || !this.object?.id) {
      return;
    }
    if (this.configured) {
      return;
    }
    this.configured = true;
    this.params = {
      id: this.object.id,
      idKey: this.chartOptionObject.apiParam + 'Id'
    };

    const chartOptions = this.ChartOptionsUtil.getOptions(this.chartOptionObject.caps);
    this.parentParam = this.chartOptionObject.apiParam;
    this.allFields = chartOptions.targetingFields;

    const eventName = 'chartDates' + chartOptions.idKey + (this.resolvedDateSelectorId || '');
    const applyChartDates = (data: any) => {
      if (this.startDate() !== 'UNDEFINED' && moment(this.startDate()).isSame(data.startDate) && moment(this.endDate()).isSame(data.endDate)) {
        return;
      }
      // Defer date bindings — lastDates / chartDates can land during parent CD
      // ('UNDEFINED' → Date NG0100 on [adnStartDate]).
      deferMarkForCheck(this.cdr, () => {
        if (this.startDate() !== 'UNDEFINED' && moment(this.startDate()).isSame(data.startDate) && moment(this.endDate()).isSame(data.endDate)) {
          return;
        }

        this.startDate.set(data.startDate);
        this.endDate.set(data.endDate);

        this.loaded = false;
        this.dataTotals = null;
        this.error = false;
        this.targetingExpression = false;

        if (this.startDate() && this.endDate()) {
          this.params.startDate = this.startDate();
          this.params.endDate = this.endDate();
        }

        this.statsResource.getDomain(this.params, (dataTotals: any) => {
          const fields = ['impressions', 'rendered', 'viewables', 'clicks'];
          const customFields = _.filter(_.keys(dataTotals.totals), (k: string) => {
            return fields.indexOf(k) < 0 && k !== 'visibles';
          });
          deferMarkForCheck(this.cdr, () => {
            this.dataTotals = dataTotals.chunks;
            this.fields = fields;
            this.customFields = customFields;
            this.loaded = true;
          });
        }, (err: any) => {
          this.adnLog.warn(err);
          deferMarkForCheck(this.cdr, () => {
            this.error = true;
          });
        }, this);
      });
    };

    this.chartDateListener = this.eventBus.on(eventName, (_event: any, data: any) => {
      applyChartDates(data);
    });

    const lastDates = this.ChartOptionsUtil.getLastChartDates(eventName);
    if (lastDates) {
      applyChartDates(lastDates);
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.chartDateListener) {
      this.chartDateListener();
    }
    if (this.canceller) {
      this.canceller.resolve('Destroyed component');
    }
  }

  fieldTranslate(key: string): string {
    return this.ogTranslate.transform(key);
  }

  sortUpdate(field: string): void {
    if (field === this.sorting.orderByParam) {
      this.sorting.asc = !this.sorting.asc;
      return;
    }
    this.sorting.orderByParam = field;
    this.sorting.asc = true;
  }

  sortedDataTotals(): any[] {
    if (!this.dataTotals) {
      return [];
    }
    const key = this.sorting.orderByParam;
    // The old template's orderBy expression treated this flag as reverse.
    const reverse = this.sorting.asc;
    return _.orderBy(this.dataTotals, (row) => {
      const val = row[key];
      if (_.get(val, 'currency')) {
        return val.amount || -1;
      }
      if (_.get(val, 'count')) {
        return val.count || -1;
      }
      return val || -1;
    }, [reverse ? 'desc' : 'asc']);
  }

  saveXls(): void {
    this.updateValues();
    new Downloader().saveXls2(this.csvTableTitle, this.downloadFields, this.downloadColumnOrder, this.dataTotals || [], this.allFields);
  }

  private updateValues(): void {
    this.downloadFields = this.fields.concat(this.customFields);
    this.downloadFields.unshift('Domain');
    this.downloadColumnOrder = this.fields.concat(this.customFields);
    this.downloadColumnOrder.unshift('domain');
    this.csvTableTitle = 'Domain Totals';
  }
}
